import React, { useEffect, useState } from "react";
import { UserPlus, ShieldCheck, Mail, RefreshCw, Trash2, AlertCircle, CheckCircle2, Copy, Heart } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/auth/AuthContext";

const ONBOARD_KINDS = ["member", "business", "venture", "professional", "ngo", "church", "community_leader"];
const EMPTY_ONBOARD = {
  email: "", full_name: "", kind: "business",
  name: "", category: "", description: "",
  phone: "", whatsapp: "", city: "", state: "",
  ras: false,
};

const OnboardTab = () => {
  const { t } = useI18n();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [form, setForm] = useState(EMPTY_ONBOARD);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);
  const [categories, setCategories] = useState([]);

  // Moderators (admin-only)
  const [moderators, setModerators] = useState([]);
  const [modForm, setModForm] = useState({ email: "", full_name: "" });
  const [modLoading, setModLoading] = useState(false);
  const [modError, setModError] = useState("");

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data.categories || [])).catch(() => {});
    if (isAdmin) loadModerators();
    // eslint-disable-next-line
  }, [isAdmin]);

  const loadModerators = async () => {
    try {
      const { data } = await api.get("/admin/moderators");
      setModerators(data.items || []);
    } catch (e) { setModError(formatApiError(e)); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submitOnboard = async (e) => {
    e.preventDefault();
    setLoading(true); setError(""); setSuccess(null);
    try {
      const payload = { ...form };
      if (payload.kind === "member") {
        delete payload.name; delete payload.category; delete payload.description;
        delete payload.phone; delete payload.whatsapp;
      }
      const { data } = await api.post("/admin/onboard", payload);
      setSuccess(data);
      setForm(EMPTY_ONBOARD);
    } catch (err) { setError(formatApiError(err)); }
    finally { setLoading(false); }
  };

  const resendEmail = async (uid) => {
    try {
      await api.post(`/admin/onboard/${uid}/resend-email`);
      alert(t.onboard.resentOk);
    } catch (e) { alert(formatApiError(e)); }
  };

  const submitModerator = async (e) => {
    e.preventDefault();
    setModLoading(true); setModError("");
    try {
      await api.post("/admin/moderators", modForm);
      setModForm({ email: "", full_name: "" });
      loadModerators();
    } catch (err) { setModError(formatApiError(err)); }
    finally { setModLoading(false); }
  };

  const deleteModerator = async (uid) => {
    if (!window.confirm(t.onboard.confirmDeleteMod)) return;
    try { await api.delete(`/admin/moderators/${uid}`); loadModerators(); }
    catch (e) { setModError(formatApiError(e)); }
  };

  const showCategory = ["business", "venture", "professional"].includes(form.kind);

  return (
    <div className="mt-6 grid lg:grid-cols-3 gap-6" data-testid="onboard-tab">
      {/* LEFT: Onboard form */}
      <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-200 p-6 md:p-8">
        <div className="flex items-center gap-3">
          <span className="w-11 h-11 rounded-full bg-orange/10 text-orange flex items-center justify-center">
            <UserPlus size={20} />
          </span>
          <div>
            <p className="eyebrow text-orange">{t.onboard.eyebrow}</p>
            <h2 className="font-display text-2xl text-teal-deep leading-tight">{t.onboard.title}</h2>
          </div>
        </div>
        <p className="text-sm text-teal-soft mt-3">{t.onboard.subtitle}</p>

        {error && <p className="mt-4 text-red-600 text-sm inline-flex items-center gap-2" data-testid="onboard-error"><AlertCircle size={14} /> {error}</p>}

        {success && (
          <div className="mt-4 bg-green-50 border border-green-200 rounded-xl p-4" data-testid="onboard-success">
            <p className="text-green-800 font-semibold inline-flex items-center gap-2"><CheckCircle2 size={16} /> {success.message}</p>
            <p className="text-xs text-green-700 mt-2">{t.onboard.tempPassword}: <code className="bg-white px-2 py-0.5 rounded">Bienvenido2026</code></p>
            <p className="text-xs text-green-700 mt-1">{t.onboard.emailSent}: <strong>{success.user.email}</strong></p>
          </div>
        )}

        <form onSubmit={submitOnboard} className="mt-5 space-y-4" data-testid="onboard-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label={t.onboard.fFullName + " *"}>
              <input required minLength={2} maxLength={120} className="field-input" value={form.full_name} onChange={set("full_name")} data-testid="onboard-fullname" />
            </Field>
            <Field label="Email *">
              <input required type="email" className="field-input" value={form.email} onChange={set("email")} data-testid="onboard-email" />
            </Field>
          </div>
          <Field label={t.onboard.fKind + " *"}>
            <select className="field-input" value={form.kind} onChange={set("kind")} data-testid="onboard-kind">
              {ONBOARD_KINDS.map((k) => (
                <option key={k} value={k}>{t.onboard.kinds[k]}</option>
              ))}
            </select>
          </Field>
          {form.kind !== "member" && (
            <>
              <Field label={t.onboard.fName + " *"} hint={t.onboard.fNameHint}>
                <input required className="field-input" value={form.name} onChange={set("name")} data-testid="onboard-name" />
              </Field>
              {showCategory && (
                <Field label={t.onboard.fCategory}>
                  <select className="field-input" value={form.category} onChange={set("category")} data-testid="onboard-category">
                    <option value="">—</option>
                    {categories.map((c) => <option key={c} value={c}>{t.categories[c] || c}</option>)}
                  </select>
                </Field>
              )}
              <Field label={t.onboard.fDescription}>
                <textarea className="field-input min-h-[80px]" value={form.description} onChange={set("description")} maxLength={2000} data-testid="onboard-description" />
              </Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label={t.onboard.fPhone}><input className="field-input" value={form.phone} onChange={set("phone")} data-testid="onboard-phone" /></Field>
                <Field label={t.onboard.fWhatsapp}><input className="field-input" value={form.whatsapp} onChange={set("whatsapp")} data-testid="onboard-whatsapp" /></Field>
              </div>
            </>
          )}
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label={t.onboard.fCity}><input className="field-input" value={form.city} onChange={set("city")} data-testid="onboard-city" /></Field>
            <Field label={t.onboard.fState}><input className="field-input" value={form.state} onChange={set("state")} data-testid="onboard-state" /></Field>
          </div>

          <label className="flex items-start gap-3 bg-rose-50 border border-rose-200 rounded-xl p-3 cursor-pointer" data-testid="onboard-ras-wrap">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 accent-rose-500"
              checked={form.ras}
              onChange={(e) => setForm({ ...form, ras: e.target.checked })}
              data-testid="onboard-ras"
            />
            <span>
              <span className="block text-sm font-bold text-rose-700 inline-flex items-center gap-1.5">
                <Heart size={13} /> {t.onboard.rasLabel}
              </span>
              <span className="block text-xs text-rose-600/80 mt-0.5">{t.onboard.rasHint}</span>
            </span>
          </label>

          <div className="bg-cream rounded-xl p-3 text-sm text-teal-soft flex items-start gap-2">
            <ShieldCheck className="text-teal shrink-0 mt-0.5" size={16} />
            <span>{t.onboard.passwordNote}</span>
          </div>

          <button type="submit" disabled={loading} className="btn-orange disabled:opacity-60 w-full justify-center" data-testid="onboard-submit">
            <UserPlus size={14} /> {loading ? t.onboard.creating : t.onboard.submit}
          </button>
        </form>
      </div>

      {/* RIGHT: Moderators (admin only) */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        {!isAdmin ? (
          <div className="text-center py-10 text-teal-soft text-sm">
            <ShieldCheck className="mx-auto mb-3 text-teal" size={28} />
            <p>{t.onboard.moderatorsAdminOnly}</p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <span className="w-9 h-9 rounded-full bg-teal/10 text-teal flex items-center justify-center">
                <ShieldCheck size={16} />
              </span>
              <div>
                <p className="eyebrow text-orange">{t.onboard.modsEyebrow}</p>
                <h3 className="font-display text-xl text-teal-deep leading-tight">{t.onboard.modsTitle}</h3>
              </div>
            </div>
            <p className="text-xs text-teal-soft mt-2">{t.onboard.modsSubtitle}</p>

            {modError && <p className="text-red-600 text-xs mt-3">{modError}</p>}

            <form onSubmit={submitModerator} className="mt-4 space-y-3" data-testid="mod-form">
              <input required type="email" placeholder="Email" className="field-input" value={modForm.email} onChange={(e) => setModForm({ ...modForm, email: e.target.value })} data-testid="mod-email" />
              <input required minLength={2} placeholder={t.onboard.fFullName} className="field-input" value={modForm.full_name} onChange={(e) => setModForm({ ...modForm, full_name: e.target.value })} data-testid="mod-fullname" />
              <button type="submit" disabled={modLoading} className="btn-teal-outline w-full justify-center" data-testid="mod-submit">
                <UserPlus size={14} /> {modLoading ? t.onboard.creating : t.onboard.modCreate}
              </button>
            </form>

            <ul className="mt-5 space-y-2" data-testid="mod-list">
              {moderators.length === 0 && (
                <li className="text-xs text-teal-soft text-center py-4">{t.onboard.modsEmpty}</li>
              )}
              {moderators.map((m) => (
                <li key={m.id} className="bg-cream rounded-xl p-3 flex items-center gap-2" data-testid={`mod-row-${m.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-teal-deep text-sm truncate">{m.full_name}</div>
                    <div className="text-xs text-teal-soft truncate">{m.email}</div>
                  </div>
                  <button type="button" onClick={() => resendEmail(m.id)} title={t.onboard.resendEmail} className="p-1.5 rounded-lg text-teal hover:bg-white" data-testid={`mod-resend-${m.id}`}>
                    <RefreshCw size={12} />
                  </button>
                  <button type="button" onClick={() => deleteModerator(m.id)} title={t.onboard.deleteMod} className="p-1.5 rounded-lg text-red-500 hover:bg-red-50" data-testid={`mod-delete-${m.id}`}>
                    <Trash2 size={12} />
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, hint, children }) => (
  <div>
    <label className="block text-xs uppercase tracking-wider text-teal-soft font-bold mb-1">{label}</label>
    {children}
    {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
  </div>
);

export default OnboardTab;
